import React, { useContext, useState } from 'react';
import './styles.scss';
import Dropdown from '../../components/Dropdown';
import { SelectMarketProps } from './interfaces';
import { ScreenEnum } from '../../enums';
import Progress from '../../components/Progress';
import { useTranslation } from 'react-i18next';
import AppStateContext from '../../AppStateProvider/context';
import Input from '../../components/Input';
import WarningOverlay from '../../components/WarningOverlay';
import { isValidUrl } from '../../utils/isValidUrl';
import { audi, bentley, bentley2, cupra, vw } from '../../assets/Icons';

export default function SelectMarket(props: SelectMarketProps) {
	const { t } = useTranslation();
	const { state, dispatch } = useContext(AppStateContext);

	const [clicks, setClicks] = useState<number>(0);
	const [lastClick, setLastClick] = useState<number | null>(null);
	const [expertMode, setExpertMode] = useState<boolean>(state.expertMode
	);
	const [showErrorOverlay, setShowErrorOverlay] = useState<boolean>(true);

	const [brand, setBrand] = useState(
		state.dataJson.brands?.find(
			(b: any) => b.name === (state.brand || 'audi')
		)
	);

	const customMarktOptions = (option: any) => ({
		...option,
		label: !option.label.includes(' ') ? (
			option.label
		) : (
			<>
				{option.label.slice(0, option.label.indexOf(' ') + 1)}
				<span className='small'>
					<br />
					{option.label.slice(option.label.indexOf(' ') + 1)}
				</span>
			</>
		),
	});

	function expertModeToggle() {
		if (lastClick === null || Date.now() - lastClick > 1000) {
			setLastClick(Date.now());
			setClicks(1);
		} else {
			if (clicks === 12) {
				const next = !expertMode;
				setExpertMode(next);
				dispatch({ type: 'SET_EXPERTMODE', value: next });
			}
			setLastClick(Date.now());
			setClicks(prev => prev + 1);

		}
	}

	const endpointsValid = Boolean(
		state.market?.urls?.pairing &&
		state.market?.urls?.delivery &&
		state.market?.urls?.vitals
	);

	const isConfirmDisabled = expertMode
		? !(
				state.market?.qsUrl &&
				isValidUrl(state.market?.qsUrl) &&
				state.brand
			)
		: !(state.brand && state.market && endpointsValid);

	return (
		<>
			<div className='top'>
				<div className='select-brand'>
					{props.backBtn(
						state.apk ? ScreenEnum.setSecond : ScreenEnum.welcome
					)}

					<h1 onClick={expertModeToggle}>
						{t('SelectMarketHeadline')}
					</h1>

					{state.errors.validationError ? (
						<p className='p1 error-message'>
							{t('SelectMarketErrorMessage')}
						</p>
					) : (
						<p className='p1'>{t('SelectMarketText')}</p>
					)}

					<Progress
						screen={ScreenEnum.selectMarket}
						setScreen={props.setScreen}
					/>

					<div className='brands'>
						{state.dataJson.brands.map((b: any, index: number) => (
							<button
								key={`brand-${index}`}
								className={`brand${state.brand === b.name ? ' active' : ''}`}
								disabled={b.disabled}
								onClick={() => {
									dispatch({
										type: 'SET_BRAND',
										value: b.name,
									});
									setBrand(
										state.dataJson.brands?.find(
											(b: any) => b.name === state.brand
										)
									);
									const nextMarket =
										b.markets?.find(
											(m: any) => m.name === 'DEU'
										) ||
										b.markets?.[0] ||
										null;
									dispatch({
										type: 'SET_MARKET',
										value: nextMarket,
									});

									if (!expertMode && nextMarket?.urls) {
										dispatch({
											type: 'SET_MARKET',
											value: {
												qsUrl: undefined,
												urls: nextMarket.urls,
											},
										});
									}
									if (expertMode && nextMarket?.qsUrl) {
										dispatch({
											type: 'SET_MARKET',
											value: {
												qsUrl: nextMarket.qsUrl,
												urls: undefined,
											},
										});
									}
								}}
							>
								{b.name === 'bentley' &&
								state.brand === 'bentley'
									? bentley2
									: b.name === 'bentley'
										? bentley
										: b.name === 'audi'
											? audi
											: b.name === 'vw'
												? vw
												: cupra}
							</button>
						))}
					</div>
				</div>

				<div className='select-market'>
					<form autoComplete='off'>
						{!expertMode && (
							<label htmlFor='market'>
								<h4>{t('SelectMarketLabelMarket')}</h4>
								<Dropdown
									list={brand?.markets || []}
									set={(marketName: any) => {
										const selected = (
											brand?.markets || []
										).find(
											(m: any) => m.name === marketName
										);
										dispatch({
											type: 'SET_MARKET',
											value: selected,
										});
										if (selected?.urls)
											dispatch({
												type: 'SET_MARKET',
												value: {
													qsUrl: undefined,
													urls: selected?.urls,
												},
											});
									}}
									defaultValue={state.market?.name || 'DEU'}
									customOptions={customMarktOptions}
								/>
							</label>
						)}

						{expertMode && (
							<Input
								labelId='label-url'
								labelHeadline={t('SelectMarketLabelURL')}
								errorMessage={
									state.errors.urlError ||
									(state.market?.qsUrl &&
										!isValidUrl(state.market?.qsUrl))
										? t('SelectMarketErrorURL')
										: undefined
								}
								type='text'
								id='url'
								value={state.market?.qsUrl || ''}
								placeholder={t('SelectMarketPlaceholderURL')}
								onInput={e => {
									const v = (e.target as HTMLInputElement)
										.value;
									dispatch({
										type: 'SET_MARKET',
										value: {
											qsUrl:
												v === 'http://' || v === 'https://'
													? v
													: v.replace(/\/+$/, ''),
											urls: undefined,
										},
									});
								}}
								required
								inputMode='url'
							/>
						)}
					</form>

					{showErrorOverlay &&
						state.errors.validationError +
							state.errors.urlError +
							state.errors.passwordError >
							5 && (
							<WarningOverlay
								headline={'ValidationFailedHeadline'}
								texts={['ValidationFailedText']}
								btnFunction={() => setShowErrorOverlay(false)}
							/>
						)}
				</div>
			</div>

			<div className='buttons'>
				<button
					className='white'
					disabled={isConfirmDisabled}
					onClick={() => {
						props.setScreen(state.setup?.pairing? ScreenEnum.final : ScreenEnum.pairDevice);

						dispatch({
							type: 'SET_MARKET',
							value: {
								qsUrl: !expertMode ? undefined : state.market?.qsUrl,
								urls: expertMode ? undefined : state.market?.urls,
							},
						});

					}}
				>
					{t('ButtonConfirm')}
				</button>
				{props.cancelBtn}
			</div>
		</>
	);
}
